/**
 * OverviewPanel — the security thesis (RD-25).
 *
 * Every CSK compliance proof is a morphism exhibition: presenting a PERMIT
 * slice morphism, a DATA.ORIGIN fibration lift, or a RES.CONSUME linear token
 * reveals it. A zero-knowledge proof lets a prover demonstrate possession of
 * such a witness without revealing it — privacy-preserving compliance audit.
 * Soundness is not statistical here: it is structural (ZKP-3), grounded in the
 * fact that a non-witness inhabits an empty type. By construction, as opposed
 * to a runtime check.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
export function OverviewPanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="The Thesis"
      subtitle="A prover demonstrates possession of a CSK witness — a PERMIT authority-DAG reachability path — without revealing the witness. Privacy-preserving compliance audit, grounded in structural soundness."
      accentColor={panelTokens.accent}
    >
      <PanelSurface.ThesisCard
        color={panelTokens.accent}
        glowColor="rgba(167,139,250,0.12)"
        quoteSource="RD-25 Zero-Knowledge Compliance Proofs · §1, §6"
        quote={
          <>
            A component proves it holds a{" "}
            <strong>PERMIT morphism</strong> — that an action is authorized —
            without revealing which subjects, objects, or authorities are
            involved. And if it is <strong>not</strong> compliant, no accepting
            transcript exists: soundness is structural, by construction, as
            opposed to a check that scans for forgeries.
          </>
        }
      >
        Every CSK compliance proof is, at its core, a{" "}
        <strong className="text-[var(--zkp-bright)]">
          morphism exhibition
        </strong>
        . PERMIT presents a slice morphism{" "}
        <code className="font-mono">f : X → A</code> in{" "}
        <code className="font-mono">Slice(A)</code>; DATA.ORIGIN presents a
        Grothendieck fibration lift; RES.CONSUME presents a linear morphism. Each
        is a constructive proof of governance compliance — but{" "}
        <em>presenting the witness reveals it</em>. In a multi-tenant cloud, a
        federated AI system, or a cross-organization pipeline, that discloses the
        authority hierarchy, the provenance chain, or the resource budget encoded
        in the witness. Yet compliance must remain auditable. A{" "}
        <strong className="text-[var(--zkp-accent)]">
          zero-knowledge proof
        </strong>{" "}
        resolves exactly this tradeoff: the prover demonstrates that a witness{" "}
        <em>exists</em> without disclosing any element of it. In CSK this is the
        propositional truncation <code className="font-mono">∥ W(T, x) ∥</code>{" "}
        (ZKP-1) — and because a non-compliant statement inhabits an{" "}
        <strong className="text-[var(--zkp-witness)]">empty witness type</strong>
        , a forged certificate would require constructing an element of nothing.
        Soundness (ZKP-3) holds with probability bound 0, by construction — not
        under a computational hardness assumption.
      </PanelSurface.ThesisCard>

      <PanelSurface.SectionHeading>
        Two guarantees, one structure
      </PanelSurface.SectionHeading>
      <PanelSurface.Callout
        title="Zero-knowledge and soundness are both structural, not enforced"
        tone="info"
        color={panelTokens.accent}
      >
        <strong>Zero-knowledge (ZKP-1):</strong> the transcript reveals nothing
        about the witness — there is no disclosure step to omit, because none
        exists in the protocol. <strong>Soundness (ZKP-3):</strong> a
        non-compliant prover cannot produce an accepting transcript — the
        accepting move is structurally absent, not stamped DENIED. Neither
        property is a runtime filter scanning for misbehavior; both are
        properties of which constructions can exist at all.
      </PanelSurface.Callout>

      <PanelSurface.SectionHeading>
        What this demo measures
      </PanelSurface.SectionHeading>
      <PanelSurface.KpiGrid>
        <PanelSurface.KpiBox
          label="RD-25 status"
          value="SETTLED"
          color={panelTokens.immuneGreen}
          subtitle="research direction settled"
        />
        <PanelSurface.KpiBox
          label="Conjectures proved"
          value="6"
          color={panelTokens.accentPurple}
          subtitle="ZKP-1 · ZKP-2 · … · ZKP-6"
        />
        <PanelSurface.KpiBox
          label="Postulates"
          value="0"
          color={panelTokens.immune}
          subtitle="across all eight Agda modules"
        />
        <PanelSurface.KpiBox
          label="Soundness bound"
          value="0"
          color={panelTokens.carrier}
          subtitle="probability of a forged accept"
        />
        <PanelSurface.KpiBox
          label="Witness disclosure"
          value="∅"
          color={panelTokens.witness}
          subtitle="reveal-move absent by construction"
        />
        <PanelSurface.KpiBox
          label="Generator circuits"
          value="4"
          color={panelTokens.accentGold}
          subtitle="G1·G2·G3·G4 — complete, minimal"
        />
      </PanelSurface.KpiGrid>

      <PanelSurface.SectionHeading>
        The compliance predicate, in one block
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Mono>{`-- The witness exists, but none is privileged (ZKP-1):
IsCompliant_T x  =  ∥ W(T, x) ∥          -- propositional truncation

-- A ZKP proves ∥ W(T, x) ∥ without revealing any element of W(T, x).

-- Structural soundness (ZKP-3):
¬ IsCompliant_T x  →  ¬ ∃ π. ZKP-Verifies (encode T x) π
-- a non-witness inhabits an empty type ⇒ no accepting transcript is constructable.`}</PanelSurface.Mono>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        How to read the rest of the demo
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.ContentList
          ariaLabel="Zero-Knowledge Compliance reading order"
          variant="ordered"
          items={[
            {
              id: "witness-map",
              title: "Witness → ZK Predicate",
              body: "Each categorical witness type — PERMIT slice morphism, DATA.ORIGIN fibration lift, PRECOMMIT graded token, RES.CONSUME linear token, SEPARATE forbidden-square absence — mapped to the ZK predicate a proof establishes and the privacy it buys.",
              color: panelTokens.witness,
            },
            {
              id: "protocol",
              title: "Commit–Challenge–Response",
              body: "The interactive protocol and its two properties — soundness and zero-knowledge — in plain English first, then the categorical statement for those who want it.",
              color: panelTokens.accentBlue,
            },
            {
              id: "prover",
              title: "Prove Without Reveal (game)",
              body: "You are a prover holding a private authority DAG. Answer the auditor's challenges by building accepting transcripts — there is no reveal-witness move and no forge-acceptance move. When the witness is absent, acceptance is unconstructable.",
              color: panelTokens.immune,
            },
            {
              id: "foundation",
              title: "Foundation & Mechanization",
              body: "The honest status: RD-25 SETTLED, the eight Agda modules, zero postulates, ZKP-1..6 proved, and the open sub-goals (ZKP-2 external DAG, ZKP-5 minimality bridge) stated as exactly what they are.",
              color: panelTokens.accentTeal,
            },
          ]}
        />
      </PanelSurface.Card>

      <PanelSurface.RepositoryLinks
        items={[
          {
            label: "RD-25 Zero-Knowledge Compliance Proofs",
            path: "research/settled/RD-25-ZERO-KNOWLEDGE-COMPLIANCE-PROOFS.md",
          },
          {
            label: "ComplianceZKP.agda (ZKP-1, ZKP-4)",
            path: "agda/Research/ZeroKnowledgeCompliance/ComplianceZKP.agda",
          },
          {
            label: "GeneratorSoundness.agda (ZKP-3 all generators)",
            path: "agda/Research/ZeroKnowledgeCompliance/GeneratorSoundness.agda",
          },
        ]}
      />
    </PanelSurface.PanelShell>
  );
}
